import { useRef, useState } from 'react';
import { Landmark, Plus, Settings2, Trash2 } from 'lucide-react';
import {
  ACCOUNT_KIND_LABELS, ACCOUNT_KINDS, FINANCE_GROUP_BY, FINANCE_METRICS, TRANSACTION_KIND_LABELS, TRANSACTION_KINDS,
  type AccountInput, type CategoryInput, type CreateTransactionInput, type FinanceTransaction, type Workspace,
} from '@unitly/contracts';
import {
  useAccountMutation, useCategoryMutation, useCreateTransaction, useDeleteAccount, useDeleteCategory, useDeleteTransaction,
  useFinanceConfig, useFinanceSettings, useTransactions, useUpdateTransaction,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { EmptyState, PageLoader, Segmented } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { WidgetBoard } from '../widgets/ConfigurableChart.tsx';

const money = (cents: number) => (cents / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const today = () => new Date().toISOString().slice(0, 10);

export function FinanceView({ workspace }: { workspace: Workspace }) {
  const { orgId } = useOrgCtx();
  const config = useFinanceConfig(orgId, workspace.id);
  const [tab, setTab] = useState<'transactions' | 'report'>('transactions');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceTransaction | 'new' | null>(null);
  const newSeq = useRef(0);
  const openNew = () => { newSeq.current += 1; setEditing('new'); };

  if (config.isLoading) return <PageLoader />;
  const cfg = config.data;
  if (!cfg?.enabled) return <EnableFinance workspace={workspace} canEnable={Boolean(cfg?.permissions.admin)} />;

  return (
    <>
      <div className="toolbar">
        <Segmented label="Sezione contabilità" value={tab} onChange={setTab} options={[{ value: 'transactions', label: 'Movimenti' }, { value: 'report', label: 'Report' }]} />
        <span className="spacer" />
        {cfg.permissions.create && tab === 'transactions' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={openNew}><Plus size={16} /> Movimento</button>
        )}
        {cfg.permissions.admin && (
          <button type="button" className="btn btn-plain btn-sm" onClick={() => setSettingsOpen(true)}><Settings2 size={16} /> Conti e categorie</button>
        )}
      </div>

      {tab === 'transactions' && <TransactionList workspace={workspace} onEdit={setEditing} />}
      {tab === 'report' && <WidgetBoard workspaceId={workspace.id} module="finance" metrics={[...FINANCE_METRICS]} groupBys={[...FINANCE_GROUP_BY]} isMoney canEdit={cfg.permissions.admin} />}

      <TransactionSheet
        key={editing === 'new' ? `new-${newSeq.current}` : (editing?.id ?? 'closed')}
        workspace={workspace} transaction={editing === 'new' ? null : editing} isNew={editing === 'new'} open={editing !== null} onClose={() => setEditing(null)}
      />
      <FinanceSettingsSheet workspace={workspace} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

function EnableFinance({ workspace, canEnable }: { workspace: Workspace; canEnable: boolean }) {
  const { orgId } = useOrgCtx();
  const enable = useFinanceSettings(orgId, workspace.id);
  const toast = useToast();
  return (
    <div className="card card-pad" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 28 }}>
      <span className="empty" style={{ padding: 0 }}><span className="icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><Landmark size={22} /></span></span>
      <strong style={{ fontSize: 18 }}>Contabilità di {workspace.name}</strong>
      <p style={{ margin: 0, color: 'var(--label-2)', maxWidth: '46ch', fontSize: 15 }}>
        Entrate e uscite per conto e categoria, con report e grafici personalizzabili.
      </p>
      {canEnable ? (
        <button type="button" className="btn btn-primary" disabled={enable.isPending}
          onClick={() => enable.mutate({ path: '/enable', method: 'POST' }, { onSuccess: () => toast('Contabilità attivata'), onError: (e) => toast(e.message) })}>
          Attiva la contabilità
        </button>
      ) : (
        <p className="hint">Chiedi a chi gestisce lo spazio di attivarla.</p>
      )}
    </div>
  );
}

function TransactionList({ workspace, onEdit }: { workspace: Workspace; onEdit: (t: FinanceTransaction | 'new') => void }) {
  const { orgId } = useOrgCtx();
  const transactions = useTransactions(orgId, workspace.id);

  if (transactions.isLoading) return <PageLoader />;
  if (transactions.data?.length === 0) {
    return <div className="card"><EmptyState tone="neutral" title="Nessun movimento">Registra la prima entrata o uscita.</EmptyState></div>;
  }
  return (
    <div className="card list">
      {transactions.data?.map((t) => (
        <button key={t.id} type="button" className="list-row" onClick={() => onEdit(t)} disabled={!t.canEdit} style={{ opacity: t.canEdit ? 1 : 0.7 }}>
          <span className="list-icon" style={{ background: t.category?.color ? `var(--ws-${t.category.color})` : 'var(--label-3)' }}>
            {t.kind === 'income' ? '+' : '−'}
          </span>
          <span className="grow">
            <div>{t.description || (t.category?.name ?? TRANSACTION_KIND_LABELS[t.kind])}</div>
            <div className="secondary">{new Date(t.occurredOn).toLocaleDateString('it-IT')} · {t.account.name}{t.category ? ` · ${t.category.name}` : ''}</div>
          </span>
          <strong style={{ color: t.kind === 'income' ? 'var(--green)' : 'var(--red)' }}>{t.kind === 'income' ? '+' : '-'}{money(t.amountCents)}</strong>
        </button>
      ))}
    </div>
  );
}

function TransactionSheet({ workspace, transaction, isNew, open, onClose }: { workspace: Workspace; transaction: FinanceTransaction | null; isNew: boolean; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const config = useFinanceConfig(orgId, workspace.id);
  const create = useCreateTransaction(orgId, workspace.id);
  const update = useUpdateTransaction(orgId, workspace.id);
  const del = useDeleteTransaction(orgId, workspace.id);
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    kind: transaction?.kind ?? 'expense' as CreateTransactionInput['kind'], accountId: transaction?.account.id ?? '', categoryId: transaction?.category?.id ?? '',
    amount: transaction ? transaction.amountCents / 100 : 0, occurredOn: transaction?.occurredOn ?? today(), description: transaction?.description ?? '',
  }));
  const [key, setKey] = useState(transaction?.id ?? 'new');
  if ((transaction?.id ?? 'new') !== key) {
    setForm({ kind: transaction?.kind ?? 'expense', accountId: transaction?.account.id ?? config.data?.accounts[0]?.id ?? '', categoryId: transaction?.category?.id ?? '', amount: transaction ? transaction.amountCents / 100 : 0, occurredOn: transaction?.occurredOn ?? today(), description: transaction?.description ?? '' });
    setKey(transaction?.id ?? 'new');
  } else if (!form.accountId && config.data?.accounts.length) {
    // The account list can still be loading when this sheet mounts; backfill once it arrives.
    setForm((f) => ({ ...f, accountId: config.data!.accounts[0]!.id }));
  }
  const categories = (config.data?.categories ?? []).filter((c) => c.kind === form.kind);

  const save = () => {
    const patch = { accountId: form.accountId, categoryId: form.categoryId || null, kind: form.kind, amountCents: Math.round(form.amount * 100), occurredOn: form.occurredOn, description: form.description };
    const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito');
    if (isNew) create.mutate(patch, { onSuccess: onClose, onError });
    else update.mutate({ id: transaction!.id, patch }, { onSuccess: onClose, onError });
  };

  return (
    <Sheet open={open} onClose={onClose} variant="center" title={isNew ? 'Nuovo movimento' : 'Modifica movimento'} labelledBy="tx-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {!isNew && transaction?.canEdit && (
            <button type="button" className="btn btn-plain" onClick={() => { if (confirm('Eliminare questo movimento?')) del.mutate(transaction.id, { onSuccess: onClose }); }}><Trash2 size={16} /></button>
          )}
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.accountId || form.amount <= 0 || create.isPending || update.isPending} onClick={save}>Salva</button>
        </div>
      }>
      <Segmented label="Tipo" value={form.kind} onChange={(kind) => setForm({ ...form, kind, categoryId: '' })} options={TRANSACTION_KINDS.map((k) => ({ value: k, label: TRANSACTION_KIND_LABELS[k] }))} />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <label className="field" style={{ flex: 1 }}><span>Importo (€)</span><input className="input" type="number" min={0} step={0.01} value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} /></label>
        <label className="field" style={{ flex: 1 }}><span>Data</span><input className="input" type="date" value={form.occurredOn} onChange={(e) => setForm({ ...form, occurredOn: e.target.value })} /></label>
      </div>
      <div className="field">
        <span>Conto</span>
        <select className="input" value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })}>
          {config.data?.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="field">
        <span>Categoria</span>
        <select className="input" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <label className="field"><span>Descrizione</span><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="es. Fattura n. 42" /></label>
    </Sheet>
  );
}

function FinanceSettingsSheet({ workspace, open, onClose }: { workspace: Workspace; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const config = useFinanceConfig(orgId, workspace.id);
  const accountMutate = useAccountMutation(orgId, workspace.id);
  const deleteAccount = useDeleteAccount(orgId, workspace.id);
  const categoryMutate = useCategoryMutation(orgId, workspace.id);
  const deleteCategory = useDeleteCategory(orgId, workspace.id);
  const toast = useToast();
  const [newAccount, setNewAccount] = useState<AccountInput>({ name: '', kind: 'bank', openingBalanceCents: 0 });
  const [newCategory, setNewCategory] = useState<CategoryInput>({ name: '', kind: 'expense', color: null });

  const onErr = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita');

  return (
    <Sheet open={open} onClose={onClose} variant="center" title="Conti e categorie" labelledBy="fin-settings-title">
      <h3 className="subhead">Conti</h3>
      <div className="list" style={{ marginBottom: 10 }}>
        {config.data?.accounts.map((a) => (
          <div key={a.id} className="list-row" style={{ cursor: 'default' }}>
            <span className="grow">{a.name}<div className="secondary">{ACCOUNT_KIND_LABELS[a.kind]} · {money(a.balanceCents)}</div></span>
            <button type="button" className="icon-btn muted" aria-label={`Elimina ${a.name}`} onClick={() => deleteAccount.mutate(a.id, { onError: onErr })}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        <input className="input" style={{ flex: 1 }} placeholder="Nuovo conto" value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} />
        <select className="input" style={{ width: 110 }} value={newAccount.kind} onChange={(e) => setNewAccount({ ...newAccount, kind: e.target.value as AccountInput['kind'] })}>
          {ACCOUNT_KINDS.map((k) => <option key={k} value={k}>{ACCOUNT_KIND_LABELS[k]}</option>)}
        </select>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!newAccount.name?.trim()}
          onClick={() => accountMutate.mutate({ patch: newAccount }, { onSuccess: () => setNewAccount({ name: '', kind: 'bank', openingBalanceCents: 0 }), onError: onErr })}>
          <Plus size={15} />
        </button>
      </div>

      <h3 className="subhead">Categorie</h3>
      <div className="list" style={{ marginBottom: 10 }}>
        {config.data?.categories.map((c) => (
          <div key={c.id} className="list-row" style={{ cursor: 'default' }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: c.color ? `var(--ws-${c.color})` : 'var(--label-3)' }} />
            <span className="grow">{c.name}<div className="secondary">{TRANSACTION_KIND_LABELS[c.kind]}</div></span>
            <button type="button" className="icon-btn muted" aria-label={`Elimina ${c.name}`} onClick={() => deleteCategory.mutate(c.id, { onError: onErr })}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" style={{ flex: 1 }} placeholder="Nuova categoria" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} />
        <select className="input" style={{ width: 110 }} value={newCategory.kind} onChange={(e) => setNewCategory({ ...newCategory, kind: e.target.value as CategoryInput['kind'] })}>
          {TRANSACTION_KINDS.map((k) => <option key={k} value={k}>{TRANSACTION_KIND_LABELS[k]}</option>)}
        </select>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!newCategory.name?.trim()}
          onClick={() => categoryMutate.mutate({ patch: newCategory }, { onSuccess: () => setNewCategory({ name: '', kind: 'expense', color: null }), onError: onErr })}>
          <Plus size={15} />
        </button>
      </div>
    </Sheet>
  );
}
